import { type ChangeEvent, type FocusEvent, useState } from 'react';
import {
  type FieldValues,
  type Path,
  type PathValue,
  type RegisterOptions,
  type UseFormRegister,
  type UseFormRegisterReturn,
  type UseFormSetValue,
  type UseFormTrigger,
} from 'react-hook-form';
import { emailRegExp } from '@/utils/validation';

interface UseEmailFieldValidationParams<
  T extends FieldValues,
  TFieldName extends Path<T> = Path<T>,
> {
  fieldName: TFieldName;
  onValueChange?: (value: string) => void;
  register: UseFormRegister<T>;
  requiredMessage: string;
  patternMessage: string;
  setValue: UseFormSetValue<T>;
  trigger: UseFormTrigger<T>;
  validate?: RegisterOptions<T, TFieldName>['validate'];
}

interface UseEmailFieldValidationResult<TFieldName extends string = string> {
  emailRegistration: UseFormRegisterReturn<TFieldName>;
  handleEmailBlur: (event: FocusEvent<HTMLInputElement>) => void;
  handleEmailChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const useEmailFieldValidation = <
  T extends FieldValues,
  TFieldName extends Path<T> = Path<T>,
>(
  params: UseEmailFieldValidationParams<T, TFieldName>
): UseEmailFieldValidationResult<TFieldName> => {
  const { fieldName, onValueChange, setValue, trigger } = params;

  const emailRegistration = params.register(fieldName, {
    required: params.requiredMessage,
    pattern: {
      value: emailRegExp,
      message: params.patternMessage,
    },
    validate: params.validate,
  });

  const [hasBlurred, setHasBlurred] = useState<boolean>(false);

  const handleEmailBlur = (event: FocusEvent<HTMLInputElement>) => {
    setHasBlurred(true);
    void emailRegistration.onBlur(event);
    void trigger(fieldName);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    onValueChange?.(value);

    setValue(fieldName, value as PathValue<T, TFieldName>, {
      shouldDirty: true,
      shouldValidate: hasBlurred,
    });
  };

  return {
    emailRegistration,
    handleEmailBlur,
    handleEmailChange,
  };
};

export default useEmailFieldValidation;
